// PetrolVibes — AI keychain + stand (upload → one design → add either or both).
// Two trigger rows (keychain, stand) share ONE popup and ONE design: the
// customer generates once in either row, and the other row then opens straight
// at "Add to cart" with the same design. No second generation.
// Config comes from data-* attributes on #pvkc and on each trigger row, and
// every customer-facing word from the JSON in #pvkcText (see
// snippets/pv-keychain.liquid), so the English, German and French stores share
// this exact file. Same class names as before, so each theme's pv-keychain.css
// styles the stand row too.
// Flow: photo → Turnstile + proof-of-work → addon-generate → poll
// addon-status → 1600x1600 design → quantity → addon-add-to-cart (product
// keychain or stand) → /cart/add.js. No email.
;(() => {
  const root = document.getElementById('pvkc')
  if (!root) return
  const cfg = root.dataset
  const SUPABASE_URL = cfg.supabaseUrl
  const ANON_KEY = cfg.anonKey
  const SITEKEY = cfg.sitekey
  const MAX_QTY = Math.max(1, parseInt(cfg.maxQty || '10', 10) || 10)
  // LAN test page only (test/index.html): replaces Turnstile, which refuses IP
  // addresses. Never set on the live store.
  const TEST_KEY = cfg.testKey || ''
  // Off the store (LAN test machine) there's no Shopify cart to add to.
  const ON_STORE = !/^(localhost|127\.0\.0\.1|192\.168\.\d+\.\d+|10\.\d+\.\d+\.\d+)$/.test(location.hostname)

  // Customer-facing text: English defaults, replaced by the snippet's language.
  // `products` holds the wording that differs between keychain and stand.
  const T = {
    verifySlow: 'Verification is taking longer than usual.',
    uploadFirst: 'Upload a photo first',
    verifyFailedLoad: "Verification didn't load — try again above",
    verifyingHuman: "Verifying you're human…",
    design: 'Design it — free',
    tooLarge: 'Image too large (max 10 MB). Please choose a smaller photo.',
    verifyFailed: 'Verification failed. Please try again.',
    verifying: 'Verifying…',
    loading: ['Analysing your car…', 'Drawing your design…', 'Cutting it out…', 'Almost done…'],
    genericError: 'Something went wrong. Please try again.',
    slow: 'Taking longer than expected. Please try again in a moment.',
    adding: 'Adding…',
    addedOne: '✓ Added to your cart',
    addedMany: '✓ Added {n} to your cart',
    offStore: '✓ Product created (variant {id}). The cart only works on the store.',
    addToCart: 'Add to cart',
    continueShopping: 'Continue shopping',
    bundleTitle: 'Bundle & save',
    bundleOne: '1 item',
    bundleMany: '{n} items',
    bundleMore: '{n}+ items',
    bundleRegular: 'Regular',
    bundleSave: 'Save {p}%',
    bundleEach: '{price} each',
    // Under the list: adds this stand, then starts the next car's design.
    bundleNext: 'Got another {vehicle}? Add this one and design your next stand →',
    vehicleWord: { car: 'car', bike: 'bike' },
    bundleFine: 'Mix & Match any acrylic stands · Applied automatically at checkout',
    bundleFineHighlight: 'Mix & Match any acrylic stands',
    products: {
      keychain: {
        inCartOne: '1 keychain in your cart — add another',
        inCartMany: '{n} keychains in your cart — add another',
        addFailed: 'Could not add your keychain to the cart. Please try again.',
        cartFailed: 'Your keychain was created, but adding it to the cart failed. Please try again.',
        alsoAdd: 'Also add this design as a keychain',
        // Desktop result step: title + short description, by vehicle.
        title: { car: 'Your Custom Car Keychain', bike: 'Your Custom Moto Keychain' },
        description: {
          car: 'Your own car, drawn from your photo and printed on a clear acrylic keychain. A small piece of your build that goes everywhere with you, and an easy gift for any car lover.',
          bike: 'Your own bike, drawn from your photo and printed on a clear acrylic keychain. Take your ride with you wherever you go, and an easy gift for any rider.',
        },
      },
      stand: {
        inCartOne: '1 stand in your cart — add another',
        inCartMany: '{n} stands in your cart — add another',
        addFailed: 'Could not add your stand to the cart. Please try again.',
        cartFailed: 'Your stand was created, but adding it to the cart failed. Please try again.',
        alsoAdd: 'Also add this design as a 30 cm stand',
        title: { car: 'Custom Car Stand', bike: 'Custom Bike Stand' },
        description: {
          car: 'Your own car, drawn from your photo and printed on clear acrylic, standing on a 30 cm base. Made for a desk, shelf or garage, and a gift any car lover will keep on display.',
          bike: 'Your own bike, drawn from your photo and printed on clear acrylic, standing on a 30 cm base. Made for a desk, shelf or garage, and a gift any rider will keep on display.',
        },
      },
    },
  }
  try {
    const custom = JSON.parse(document.getElementById('pvkcText')?.textContent || '{}')
    const { products, ...rest } = custom
    Object.assign(T, rest)
    for (const p of Object.keys(products || {})) T.products[p] = { ...T.products[p], ...products[p] }
  } catch (e) { console.error('pvkc: bad text JSON', e) }
  const fill = (text, values) => text.replace(/\{(\w+)\}/g, (m, k) => (k in values ? values[k] : m))

  const fnUrl = (name) => `${SUPABASE_URL}/functions/v1/${name}`
  const auth = { Authorization: `Bearer ${ANON_KEY}`, apikey: ANON_KEY }
  const $ = (id) => document.getElementById(id)

  const overlay = $('pvkcOverlay')
  const toastEl = $('pvkcToast')
  // A transformed ancestor (sticky headers, drawers…) would trap position:fixed;
  // living on <body> avoids whatever the theme wraps the section in.
  document.body.appendChild(overlay)
  document.body.appendChild(toastEl)

  // One trigger row per product; each carries its own price, popup headings
  // and (once added) its own "in your cart" line.
  const triggers = {}
  root.querySelectorAll('.pvkc-trigger[data-product]').forEach((el) => { triggers[el.dataset.product] = el })
  const PRODUCTS = Object.keys(triggers)

  const state = {
    file: null,
    design: null,          // { jobId, imageUrl }: shared by every product
    product: 'keychain',   // the row the popup was opened from
    busy: false,
    autoGenerate: false,   // "Generate again" waits for a fresh Turnstile token
    qty: {},               // per product
    addedTotal: {},        // per product, this visit
    addedFor: {},          // per product: the design id last added to the cart
    addedQty: {},          // per product: how many of that design are in the cart
    variantFor: {},        // per product: { jobId, variantId } of the last product made
  }
  PRODUCTS.forEach((p) => {
    state.qty[p] = 1; state.addedTotal[p] = 0; state.addedFor[p] = null; state.addedQty[p] = 0
    // Remember the row's own label, to put back if its product is taken out again.
    triggers[p].dataset.text = triggers[p].querySelector('.pvkc-trigger-text').textContent
  })
  const priceOf = (p) => triggers[p]?.dataset.price || ''
  const textOf = (p) => T.products[p] || T.products.keychain

  // ── Toast ────────────────────────────────────────────────────
  let toastTimer
  function toast(message, isError = false) {
    toastEl.textContent = message
    toastEl.classList.toggle('is-error', isError)
    toastEl.classList.add('is-shown')
    clearTimeout(toastTimer)
    toastTimer = setTimeout(() => toastEl.classList.remove('is-shown'), 4500)
  }

  // ── Product shown in the popup ───────────────────────────────
  function applyProduct() {
    const t = triggers[state.product]
    $('pvkcEyebrow').textContent = t.dataset.eyebrow || ''
    $('pvkcTitle').textContent = t.dataset.headline || ''
    overlay.querySelectorAll('[data-for]').forEach((el) => { el.hidden = el.dataset.for !== state.product })
    const gallery = overlay.querySelector(`.pvkc-gallery[data-for="${state.product}"]`)
    $('pvkcExamples').hidden = !gallery || !gallery.querySelector('img')
  }

  // ── Open / close ─────────────────────────────────────────────
  // Opening a row with a design already made skips straight to Add to cart.
  function openTool(product) {
    if (product) state.product = product
    applyProduct()
    overlay.hidden = false
    document.documentElement.classList.add('pvkc-lock')
    loadTurnstile()
    if (state.design && $('pvkcViewLoading').hidden) showResult()
    else if (!$('pvkcViewUpload').hidden) startTurnstileWatchdog()
  }
  function closeTool() {
    overlay.hidden = true
    document.documentElement.classList.remove('pvkc-lock')
    clearTimeout(ts.timer)
  }
  PRODUCTS.forEach((p) => triggers[p].addEventListener('click', () => openTool(p)))
  $('pvkcClose').addEventListener('click', closeTool)
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !overlay.hidden) closeTool() })

  // Desktop result step: the design and its examples go into one left-hand
  // column (#pvkcLeft), so the examples sit right under the design. On a
  // phone they stay exactly where the markup has them.
  const wideQuery = window.matchMedia('(min-width: 900px)')
  function placeLeftColumn() {
    const left = $('pvkcLeft'), result = $('pvkcViewResult')
    const art = $('pvkcArt').parentElement, examples = $('pvkcExamples')
    if (wideQuery.matches) { left.append(art, examples); return }
    if (art.parentElement === left) { left.after(art); result.append(examples) }
  }
  if (wideQuery.addEventListener) wideQuery.addEventListener('change', placeLeftColumn)

  // ── Views + steps ────────────────────────────────────────────
  function showView(name) {
    ;['Upload', 'Loading', 'Result'].forEach((v) => { $(`pvkcView${v}`).hidden = v !== name })
    const step = { Upload: 1, Loading: 2, Result: 3 }[name]
    ;[1, 2, 3].forEach((i) => {
      $(`pvkcStep${i}`).classList.toggle('is-active', i === step)
      $(`pvkcStep${i}`).classList.toggle('is-done', i < step)
    })
    // The result step uses the wider two-column layout on desktop (snippet CSS).
    overlay.querySelector('.pvkc-panel').classList.toggle('is-wide', name === 'Result')
    placeLeftColumn()
    if (name === 'Upload') startTurnstileWatchdog()
    else clearTimeout(ts.timer)
  }

  // ── Turnstile (skipped entirely in LAN test mode) ────────────
  const ts = { token: null, widgetId: null, failed: false, timer: null, loadBegun: false }
  const TS_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=pvkcTurnstileOnload'
  const TS_TIMEOUT_MS = 20000
  const TS_RETRY_MS = [1500, 4000]

  function showTurnstileHelp(show) {
    $('pvkcTurnstileHelp').hidden = !show
    if (show) $('pvkcTurnstileHelpText').textContent = T.verifySlow
  }
  function startTurnstileWatchdog() {
    clearTimeout(ts.timer)
    if (TEST_KEY || ts.token || overlay.hidden) return
    ts.timer = setTimeout(function check() {
      if (ts.token) return
      if (document.visibilityState === 'hidden') { ts.timer = setTimeout(check, TS_TIMEOUT_MS); return }
      ts.failed = true
      showTurnstileHelp(true)
      updateGenerateButton()
    }, TS_TIMEOUT_MS)
  }
  // Loaded on first open, so product pages don't pull Cloudflare in for nothing.
  function loadTurnstile(attempt = 0) {
    if (TEST_KEY) { $('pvkcTurnstile').hidden = true; return }
    if (attempt === 0 && ts.loadBegun) return
    ts.loadBegun = true
    const script = document.createElement('script')
    script.src = TS_SCRIPT
    script.async = true
    script.onerror = () => {
      script.remove()
      if (window.turnstile) return
      if (attempt < TS_RETRY_MS.length) { setTimeout(() => loadTurnstile(attempt + 1), TS_RETRY_MS[attempt]); return }
      ts.failed = true
      showTurnstileHelp(true)
      updateGenerateButton()
    }
    document.head.appendChild(script)
  }
  window.pvkcTurnstileOnload = () => {
    if (ts.widgetId !== null) return
    ts.widgetId = window.turnstile.render('#pvkcTurnstile', {
      sitekey: SITEKEY,
      theme: 'light',
      size: 'flexible',
      callback: (token) => {
        ts.token = token
        ts.failed = false
        clearTimeout(ts.timer)
        showTurnstileHelp(false)
        updateGenerateButton()
        if (state.autoGenerate) { state.autoGenerate = false; generate() }
      },
      'error-callback': () => { ts.token = null; ts.failed = true; showTurnstileHelp(true); updateGenerateButton() },
      'expired-callback': () => { ts.token = null; updateGenerateButton() },
      'timeout-callback': () => { ts.token = null; updateGenerateButton() },
    })
  }
  function resetTurnstile() {
    ts.token = null
    if (window.turnstile && ts.widgetId !== null) window.turnstile.reset(ts.widgetId)
    updateGenerateButton()
  }
  $('pvkcTurnstileRetry').addEventListener('click', () => {
    ts.failed = false
    showTurnstileHelp(false)
    if (window.turnstile && ts.widgetId !== null) resetTurnstile()
    else { ts.loadBegun = false; loadTurnstile() }
    startTurnstileWatchdog()
    updateGenerateButton()
  })
  const verified = () => !!TEST_KEY || !!ts.token

  // ── Generate button ──────────────────────────────────────────
  function updateGenerateButton() {
    const btn = $('pvkcGenerate')
    if (state.busy) return
    if (!state.file) { btn.disabled = true; btn.textContent = T.uploadFirst; return }
    if (!verified() && ts.failed) { btn.disabled = true; btn.textContent = T.verifyFailedLoad; return }
    if (!verified()) { btn.disabled = true; btn.innerHTML = '<span class="pvkc-btn-spinner"></span>'; btn.append(T.verifyingHuman); return }
    btn.disabled = false
    btn.textContent = T.design
  }

  // ── Photo in ─────────────────────────────────────────────────
  const MAX_UPLOAD_DIMENSION = 2048
  const MAX_UPLOAD_BYTES = 10 * 1024 * 1024
  async function shrinkIfNeeded(file) {
    if (file.type === 'image/tiff' || /\.tiff?$/i.test(file.name)) return file
    try {
      const bitmap = await createImageBitmap(file)
      if (bitmap.width <= MAX_UPLOAD_DIMENSION && bitmap.height <= MAX_UPLOAD_DIMENSION) { bitmap.close(); return file }
      const scale = MAX_UPLOAD_DIMENSION / Math.max(bitmap.width, bitmap.height)
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(bitmap.width * scale)
      canvas.height = Math.round(bitmap.height * scale)
      canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height)
      bitmap.close()
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9))
      return blob ? new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }) : file
    } catch (e) {
      console.error('pvkc: resize failed, uploading original', e)
      return file
    }
  }
  async function takePhoto(file) {
    if (!file || !file.type.startsWith('image/') && !/\.(tiff?|avif)$/i.test(file.name)) return
    const shrunk = await shrinkIfNeeded(file)
    if (shrunk.size > MAX_UPLOAD_BYTES) { toast(T.tooLarge, true); return }
    state.file = shrunk
    const reader = new FileReader()
    reader.onload = (ev) => { $('pvkcThumb').src = ev.target.result }
    reader.readAsDataURL(shrunk)
    $('pvkcDrop').hidden = true
    $('pvkcThumbWrap').hidden = false
    updateGenerateButton()
  }
  function clearPhoto() {
    state.file = null
    $('pvkcFile').value = ''
    $('pvkcThumb').removeAttribute('src')
    $('pvkcDrop').hidden = false
    $('pvkcThumbWrap').hidden = true
    updateGenerateButton()
  }
  $('pvkcFile').addEventListener('change', (e) => takePhoto(e.target.files[0]))
  $('pvkcRemove').addEventListener('click', clearPhoto)
  const drop = $('pvkcDrop')
  drop.addEventListener('dragover', (e) => { e.preventDefault(); drop.classList.add('is-dragging') })
  drop.addEventListener('dragleave', () => drop.classList.remove('is-dragging'))
  drop.addEventListener('drop', (e) => {
    e.preventDefault()
    drop.classList.remove('is-dragging')
    takePhoto(e.dataTransfer.files[0])
  })
  document.addEventListener('paste', (e) => {
    if (overlay.hidden || state.file || $('pvkcViewUpload').hidden) return
    const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith('image/'))
    if (item) takePhoto(item.getAsFile())
  })

  // ── SHA-256 (proof-of-work + fingerprint) ────────────────────
  // crypto.subtle only exists on HTTPS/localhost; this pure-JS version covers
  // plain-http LAN testing. On the live store crypto.subtle is used.
  function pvkcSha256(msg) {
    const K = [0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
      0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
      0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
      0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
      0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
      0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
      0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
      0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2]
    const len = msg.length
    const padded = ((len + 9 + 63) >> 6) << 6
    const m = new Uint8Array(padded)
    m.set(msg); m[len] = 0x80
    const dv = new DataView(m.buffer)
    dv.setUint32(padded - 4, (len * 8) >>> 0)
    dv.setUint32(padded - 8, Math.floor((len * 8) / 0x100000000))
    let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a
    let h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19
    const w = new Uint32Array(64)
    for (let o = 0; o < padded; o += 64) {
      for (let i = 0; i < 16; i++) w[i] = dv.getUint32(o + i * 4)
      for (let i = 16; i < 64; i++) {
        const x = w[i - 15], y = w[i - 2]
        const s0 = ((x >>> 7) | (x << 25)) ^ ((x >>> 18) | (x << 14)) ^ (x >>> 3)
        const s1 = ((y >>> 17) | (y << 15)) ^ ((y >>> 19) | (y << 13)) ^ (y >>> 10)
        w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0
      }
      let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7
      for (let i = 0; i < 64; i++) {
        const S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7))
        const t1 = (h + S1 + ((e & f) ^ (~e & g)) + K[i] + w[i]) | 0
        const S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10))
        const t2 = (S0 + ((a & b) ^ (a & c) ^ (b & c))) | 0
        h = g; g = f; f = e; e = (d + t1) | 0; d = c; c = b; b = a; a = (t1 + t2) | 0
      }
      h0 = (h0 + a) | 0; h1 = (h1 + b) | 0; h2 = (h2 + c) | 0; h3 = (h3 + d) | 0
      h4 = (h4 + e) | 0; h5 = (h5 + f) | 0; h6 = (h6 + g) | 0; h7 = (h7 + h) | 0
    }
    const out = new Uint8Array(32), odv = new DataView(out.buffer)
    ;[h0, h1, h2, h3, h4, h5, h6, h7].forEach((v, i) => odv.setUint32(i * 4, v >>> 0))
    return out
  }
  async function pvkcDigest(bytes) {
    if (self.crypto && self.crypto.subtle) return new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))
    return pvkcSha256(bytes)
  }

  // ── Proof-of-work (shared pow-challenge function) ────────────
  const POW_WORKER_SRC = pvkcSha256.toString() + '\n' + pvkcDigest.toString() + `
    function leadingZeroBits(bytes) {
      let count = 0
      for (const byte of bytes) {
        if (byte === 0) { count += 8; continue }
        for (let i = 7; i >= 0; i--) { if ((byte >> i) & 1) return count; count++ }
        return count
      }
      return count
    }
    self.onmessage = async (e) => {
      const { id, difficulty, expiresAt } = e.data
      const enc = new TextEncoder()
      for (let nonce = 0; ; nonce++) {
        if (leadingZeroBits(await pvkcDigest(enc.encode(id + ':' + expiresAt + ':' + difficulty + ':' + nonce))) >= difficulty) {
          self.postMessage({ nonce }); return
        }
      }
    }`
  function solvePow(id, difficulty, expiresAt) {
    return new Promise((resolve, reject) => {
      const worker = new Worker(URL.createObjectURL(new Blob([POW_WORKER_SRC], { type: 'application/javascript' })))
      worker.onmessage = (e) => { resolve(e.data.nonce); worker.terminate() }
      worker.onerror = (e) => { reject(e); worker.terminate() }
      worker.postMessage({ id, difficulty, expiresAt })
    })
  }
  async function deviceFingerprint() {
    const parts = []
    try {
      const canvas = document.createElement('canvas')
      canvas.width = 220; canvas.height = 40
      const ctx = canvas.getContext('2d')
      ctx.textBaseline = 'top'; ctx.font = '14px "Arial"'; ctx.fillStyle = '#f60'; ctx.fillRect(0, 0, 220, 40)
      ctx.fillStyle = '#069'; ctx.fillText('PVKC-fp 🚗 keychain 0123', 2, 2)
      parts.push(canvas.toDataURL())
    } catch { parts.push('') }
    try { parts.push(`${screen.width}x${screen.height}x${screen.colorDepth}`) } catch { parts.push('') }
    try { parts.push(String(navigator.hardwareConcurrency || '')) } catch { parts.push('') }
    try {
      const hash = await pvkcDigest(new TextEncoder().encode(parts.join('|')))
      return Array.from(hash).map((b) => b.toString(16).padStart(2, '0')).join('')
    } catch { return '' }
  }

  // ── Generate (one design for every product) ──────────────────
  const LOADING_LABELS = T.loading
  async function generate() {
    if (!state.file || state.busy || !verified()) return
    state.busy = true
    const btn = $('pvkcGenerate')
    btn.disabled = true
    btn.innerHTML = '<span class="pvkc-btn-spinner"></span>'; btn.append(T.verifying)
    let pow, fp
    try {
      const [chRes, fingerprint] = await Promise.all([fetch(fnUrl('pow-challenge'), { headers: auth }), deviceFingerprint()])
      if (!chRes.ok) throw new Error('challenge request failed')
      const ch = await chRes.json()
      pow = { ...ch, nonce: await solvePow(ch.id, ch.difficulty, ch.expiresAt) }
      fp = fingerprint
    } catch (e) {
      state.busy = false
      updateGenerateButton()
      toast(T.verifyFailed, true)
      return
    }
    const form = new FormData()
    form.append('image', state.file)
    if (TEST_KEY) form.append('test_key', TEST_KEY)
    else form.append('turnstile_token', ts.token)
    form.append('pow_id', pow.id)
    form.append('pow_difficulty', String(pow.difficulty))
    form.append('pow_expires_at', String(pow.expiresAt))
    form.append('pow_signature', pow.signature)
    form.append('pow_nonce', String(pow.nonce))
    if (fp) form.append('device_fp', fp)

    showView('Loading')
    let li = 0
    $('pvkcLoadingLabel').textContent = LOADING_LABELS[0]
    const labelTimer = setInterval(() => { li++; $('pvkcLoadingLabel').textContent = LOADING_LABELS[li % LOADING_LABELS.length] }, 6000)
    try {
      const res = await fetch(fnUrl('addon-generate'), { method: 'POST', headers: auth, body: form })
      const data = await res.json().catch(() => ({}))
      resetTurnstile() // tokens are single-use
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`)
      const job = await pollJob(data.job_id)
      if (job.status === 'failed') throw new Error(job.error || T.genericError)
      state.design = { jobId: data.job_id, imageUrl: job.image_url, vehicle: job.vehicle === 'bike' ? 'bike' : 'car' }
      showResult()
    } catch (e) {
      showView('Upload')
      toast(e.message || T.genericError, true)
    } finally {
      clearInterval(labelTimer)
      state.busy = false
      updateGenerateButton()
    }
  }
  async function pollJob(jobId) {
    const startedAt = Date.now()
    while (Date.now() - startedAt < 5 * 60 * 1000) {
      const res = await fetch(`${fnUrl('addon-status')}?job_id=${encodeURIComponent(jobId)}`, { headers: auth })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`)
      if (data.status === 'ready' || data.status === 'done' || data.status === 'failed') return data
      await new Promise((r) => setTimeout(r, 2500))
    }
    throw new Error(T.slow)
  }
  $('pvkcGenerate').addEventListener('click', generate)

  // ── Bundle & save (products with data-bundles, i.e. the stand) ──
  // "2:5,3:10,4:15" = 2 → 5% off, 3 → 10%, 4 or more → 15%. Display only:
  // the store's automatic discounts do the charging at checkout.
  const bundlesOf = (p) => (triggers[p]?.dataset.bundles || '').split(',')
    .map((x) => x.split(':').map(Number)).filter(([q, pct]) => q > 1 && pct > 0)
    .sort((a, b) => a[0] - b[0])
  const bundlePct = (p, qty) => bundlesOf(p).reduce((pct, [q, off]) => (qty >= q ? off : pct), 0)
  const unitCents = (p) => parseInt(triggers[p]?.dataset.priceCents || '0', 10)
  // Shopify money format, e.g. "${{amount}}" or "{{amount_with_comma_separator}} €".
  function money(cents) {
    const fmt = cfg.moneyFormat || '${{amount}}'
    const n = (cents / 100).toFixed(2)
    const group = (s, sep) => s.replace(/\B(?=(\d{3})+(?!\d))/g, sep)
    const [i, d] = n.split('.')
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) => ({
      amount: `${group(i, ',')}.${d}`,
      amount_no_decimals: group(String(Math.round(cents / 100)), ','),
      amount_with_comma_separator: `${group(i, '.')},${d}`,
      amount_no_decimals_with_comma_separator: group(String(Math.round(cents / 100)), '.'),
      amount_with_apostrophe_separator: `${group(i, "'")}.${d}`,
    }[k] ?? n))
  }
  function renderBundles() {
    const p = state.product, tiers = bundlesOf(p)
    const box = $('pvkcBundles')
    box.hidden = !tiers.length || isAdded()
    $('pvkcBuyRowQty').hidden = tiers.length > 0
    if (!tiers.length) return
    const unit = unitCents(p), qty = state.qty[p]
    const rows = [[1, 0], ...tiers]
    const activeQ = rows.reduce((a, [q]) => (qty >= q ? q : a), 1)
    box.innerHTML = ''
    const title = document.createElement('div')
    title.className = 'pvkc-bundles-title'
    title.textContent = T.bundleTitle
    box.append(title, bundleFine())
    rows.forEach(([q, pct], idx) => {
      const last = idx === rows.length - 1 && q > 1
      const gross = unit * q, total = Math.round(gross * (1 - pct / 100))
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'pvkc-bundle' + (q === activeQ ? ' is-active' : '')
      btn.setAttribute('aria-pressed', String(q === activeQ))
      const code = document.createElement('span'); code.className = 'pvkc-bundle-code'; code.textContent = last ? `${q}+` : String(q)
      const main = document.createElement('span'); main.className = 'pvkc-bundle-main'
      const name = document.createElement('span'); name.className = 'pvkc-bundle-name'
      name.append(q === 1 ? T.bundleOne : fill(last ? T.bundleMore : T.bundleMany, { n: q }))
      if (pct) { const pill = document.createElement('span'); pill.className = 'pvkc-bundle-pill'; pill.textContent = fill(T.bundleSave, { p: pct }); name.append(pill) }
      const sub = document.createElement('span'); sub.className = 'pvkc-bundle-sub'
      sub.textContent = q === 1 ? T.bundleRegular : fill(T.bundleEach, { price: money(Math.round(total / q)) })
      main.append(name, sub)
      const amt = document.createElement('span'); amt.className = 'pvkc-bundle-amt'
      const tot = document.createElement('span'); tot.className = 'pvkc-bundle-total'; tot.textContent = money(total)
      amt.append(tot)
      if (q > 1) { const was = document.createElement('s'); was.className = 'pvkc-bundle-was'; was.textContent = money(gross); amt.append(was) }
      btn.append(code, main, amt)
      btn.addEventListener('click', () => setQty(q))
      box.append(btn)
    })
    // Mixing cars: put this stand in the cart, then straight on to the next design.
    const nextCar = document.createElement('button')
    nextCar.type = 'button'
    nextCar.className = 'pvkc-link pvkc-bundle-next'
    nextCar.textContent = fill(T.bundleNext, { vehicle: T.vehicleWord[state.design?.vehicle] || T.vehicleWord.car })
    nextCar.addEventListener('click', designNextCar)
    box.append(nextCar)
  }
  // Small print under the heading; the highlighted part (different designs
  // count too) wiggles once each time the stand view opens.
  function bundleFine() {
    const box = $('pvkcBundles')
    const fine = document.createElement('p')
    fine.className = 'pvkc-bundle-fine'
    const [before, after] = T.bundleFine.split(T.bundleFineHighlight)
    if (after === undefined || !T.bundleFineHighlight) { fine.textContent = T.bundleFine; return fine }
    const wiggle = document.createElement('span')
    wiggle.className = 'pvkc-wiggle'
    wiggle.textContent = T.bundleFineHighlight
    fine.append(before, wiggle, after)
    if (!box.dataset.wiggled) { box.dataset.wiggled = '1'; setTimeout(() => wiggle.classList.add('is-wiggling'), 600) }
    return fine
  }
  async function designNextCar() {
    if (!state.design || state.busy) return
    if (!isAdded()) await addToCart()
    if (!isAdded()) return // adding failed; the toast says why
    state.design = null
    clearPhoto()
    showView('Upload')
  }

  // ── Result: quantity + add to cart (per product) ─────────────
  function setQty(n) {
    const p = state.product
    state.qty[p] = Math.min(MAX_QTY, Math.max(1, parseInt(n, 10) || 1))
    const qty = state.qty[p]
    $('pvkcQty').value = qty
    $('pvkcQtyMinus').disabled = qty <= 1
    $('pvkcQtyPlus').disabled = qty >= MAX_QTY
    const pct = bundlePct(p, qty)
    $('pvkcPrice').textContent = bundlesOf(p).length && unitCents(p)
      ? money(Math.round(unitCents(p) * qty * (1 - pct / 100)))
      : (qty > 1 && priceOf(p) ? `${priceOf(p)} × ${qty}` : priceOf(p))
    // Desktop top block: the same price, struck through next to it either the
    // bundle's full price (2+ in a bundle) or the compare-at price.
    $('pvkcTopPrice').textContent = $('pvkcPrice').textContent
    const compare = pct && unitCents(p) ? money(unitCents(p) * qty) : (qty === 1 ? triggers[p].dataset.compare || '' : '')
    $('pvkcTopCompare').textContent = compare
    $('pvkcTopCompare').hidden = !compare
    renderBundles()
  }
  $('pvkcQtyMinus').addEventListener('click', () => setQty(state.qty[state.product] - 1))
  $('pvkcQtyPlus').addEventListener('click', () => setQty(state.qty[state.product] + 1))
  $('pvkcQty').addEventListener('change', (e) => setQty(e.target.value))

  function showResult() {
    applyProduct()
    delete $('pvkcBundles').dataset.wiggled
    // Title + description beside the design (desktop only, see the snippet CSS).
    const t = textOf(state.product), v = state.design.vehicle || 'car'
    $('pvkcProductTitle').textContent = (t.title && t.title[v]) || ''
    $('pvkcDesc').textContent = (t.description && t.description[v]) || ''
    $('pvkcArt').src = state.design.imageUrl
    setQty(state.qty[state.product])
    $('pvkcStatus').textContent = ''
    setSplit(state.addedFor[state.product] === state.design.jobId)
    updateAlso()
    showView('Result')
  }
  // After the first add: "Make another" | "Continue shopping" (closes the
  // tool). The quantity row hides then, since this design is in the cart.
  function setSplit(on) {
    $('pvkcActions').classList.toggle('is-split', on)
    $('pvkcAnother').hidden = !on
    $('pvkcAdd').textContent = on ? T.continueShopping : T.addToCart
    document.querySelector('.pvkc-buy-row').hidden = on
    $('pvkcBundles').hidden = on || !bundlesOf(state.product).length
  }
  const isAdded = () => !!state.design && state.addedFor[state.product] === state.design.jobId

  // A trigger row's tick + "N in your cart" line, from what this visit added.
  function updateTrigger(p) {
    const t = triggers[p], n = state.addedTotal[p]
    t.classList.toggle('is-added', n > 0)
    t.querySelector('.pvkc-trigger-text').textContent = n === 0
      ? t.dataset.text
      : n === 1 ? textOf(p).inCartOne : fill(textOf(p).inCartMany, { n })
  }
  function markAdded(p, count) {
    state.addedTotal[p] += count
    state.addedQty[p] = (state.addedFor[p] === state.design.jobId ? state.addedQty[p] : 0) + count
    state.addedFor[p] = state.design.jobId
    updateTrigger(p)
    if (p === state.product) setSplit(true)
    updateAlso()
  }

  // Makes (or reuses) the product for this design and puts `qty` in the cart.
  async function addProduct(p, qty) {
    const res = await fetch(fnUrl('addon-add-to-cart'), {
      method: 'POST',
      headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ job_id: state.design.jobId, product: p }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.variant_id) throw new Error(data.error || textOf(p).addFailed)
    state.variantFor[p] = { jobId: state.design.jobId, variantId: String(data.variant_id) }
    if (ON_STORE) {
      const cartRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(data.variant_id), quantity: qty }] }),
      })
      if (!cartRes.ok) throw new Error(textOf(p).cartFailed)
      // Motion theme: cart:build rebuilds the cart drawer and the header cart
      // count without opening the drawer over the tool.
      document.dispatchEvent(new CustomEvent('cart:build'))
    }
    return data.variant_id
  }
  // Takes this design's product `p` back out of the cart (the checkbox).
  async function removeProduct(p) {
    const v = state.variantFor[p]
    if (ON_STORE && v && v.jobId === state.design.jobId) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: v.variantId, quantity: 0 }),
      })
      if (!res.ok) throw new Error(T.genericError)
      document.dispatchEvent(new CustomEvent('cart:build'))
    }
    state.addedTotal[p] = Math.max(0, state.addedTotal[p] - state.addedQty[p])
    state.addedQty[p] = 0
    state.addedFor[p] = null
    updateTrigger(p)
  }

  // "Also add this design as a stand / keychain" checkbox: ticking adds 1 of
  // the other product straight to the cart (same design, no new generation);
  // unticking takes it back out. It shows ticked whenever the other product
  // of this design is in the cart, however it got there.
  const otherProduct = () => PRODUCTS.find((p) => p !== state.product)
  function updateAlso() {
    const other = otherProduct()
    $('pvkcAlso').hidden = !other || !state.design
    if (!other || !state.design) return
    const t = textOf(other)
    $('pvkcAlsoText').textContent = fill(t.alsoAdd || t.switchTo, { price: '' }).replace(/s*·s*$/, '')
    $('pvkcAlsoPrice').textContent = priceOf(other)
    $('pvkcAlsoBox').checked = state.addedFor[other] === state.design.jobId
  }
  $('pvkcAlsoBox').addEventListener('change', async (e) => {
    const box = e.target, other = otherProduct()
    // Nothing to add yet (or still busy): just undo the click, never hide the box.
    if (!other || !state.design || state.busy) { box.checked = !box.checked; return }
    state.busy = true
    box.disabled = true
    $('pvkcAlso').classList.add('is-busy')
    try {
      if (box.checked) {
        const variantId = await addProduct(other, 1)
        if (!ON_STORE) $('pvkcStatus').textContent = fill(T.offStore, { id: variantId })
        markAdded(other, 1)
      } else {
        await removeProduct(other)
      }
    } catch (err) {
      toast(err.message || T.genericError, true)
    } finally {
      state.busy = false
      box.disabled = false
      $('pvkcAlso').classList.remove('is-busy')
      updateAlso()
    }
  })

  async function addToCart() {
    if (isAdded()) { closeTool(); return } // "Continue shopping"
    if (!state.design || state.busy) return
    state.busy = true
    const p = state.product
    const btn = $('pvkcAdd')
    btn.disabled = true
    btn.innerHTML = '<span class="pvkc-btn-spinner"></span>'; btn.append(T.adding)
    $('pvkcStatus').textContent = ''
    const qty = state.qty[p]
    try {
      const variantId = await addProduct(p, qty)
      $('pvkcStatus').textContent = ON_STORE
        ? (qty === 1 ? T.addedOne : fill(T.addedMany, { n: qty }))
        : fill(T.offStore, { id: variantId })
      markAdded(p, qty)
    } catch (e) {
      toast(e.message || T.genericError, true)
    } finally {
      state.busy = false
      btn.disabled = false
      btn.textContent = isAdded() ? T.continueShopping : T.addToCart
    }
  }
  $('pvkcAdd').addEventListener('click', addToCart)

  // "Make another": fresh photo, fresh design (for every product); what's in
  // the cart stays.
  $('pvkcAnother').addEventListener('click', () => {
    state.design = null
    clearPhoto()
    showView('Upload')
  })
  // "Generate again": same photo, new design (for every product).
  $('pvkcRetry').addEventListener('click', () => {
    state.design = null
    showView('Upload')
    if (verified()) generate()
    else state.autoGenerate = true
  })

  // Testing without paying for a new design: reopen one that already exists,
  // with everything working (Add to cart, checkbox, bundles). Read-only: it
  // never generates. Either open the page with
  //   ?pvkc_design=<design id>&pvkc_product=stand   (or keychain)
  // or run pvkcLoadDesign('<design id>', 'stand') in the console.
  async function loadDesign(jobId, product = 'stand') {
    const res = await fetch(`${fnUrl('addon-status')}?job_id=${encodeURIComponent(jobId)}`, { headers: auth })
    const job = await res.json().catch(() => ({}))
    if (!res.ok || !job.image_url) { toast(job.error || T.genericError, true); return }
    state.design = { jobId, imageUrl: job.image_url, vehicle: job.vehicle === 'bike' ? 'bike' : 'car' }
    openTool(triggers[product] ? product : state.product)
  }
  window.pvkcLoadDesign = loadDesign
  const params = new URLSearchParams(location.search)
  if (params.get('pvkc_design')) loadDesign(params.get('pvkc_design'), params.get('pvkc_product') || 'stand')

  updateGenerateButton()
})()
